import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { teacherLatenessAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

const extractError = (err, fallback = 'An error occurred') => getErrorMessage(err, fallback);

export const fetchLatenessConfig = createAsyncThunk(
  'teacherLateness/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getLatenessConfig();
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to fetch lateness config'));
    }
  }
);

export const updateLatenessConfig = createAsyncThunk(
  'teacherLateness/updateConfig',
  async (data, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.updateLatenessConfig(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to update config'));
    }
  }
);

export const fetchHolidays = createAsyncThunk(
  'teacherLateness/fetchHolidays',
  async (_, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getHolidays();
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to fetch holidays'));
    }
  }
);

export const createHoliday = createAsyncThunk(
  'teacherLateness/createHoliday',
  async (data, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.createHoliday(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to create holiday'));
    }
  }
);

export const deleteHoliday = createAsyncThunk(
  'teacherLateness/deleteHoliday',
  async (id, { rejectWithValue }) => {
    try {
      await teacherLatenessAPI.deleteHoliday(id);
      return id;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to delete holiday'));
    }
  }
);

export const fetchDeductionPolicies = createAsyncThunk(
  'teacherLateness/fetchPolicies',
  async (params, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getDeductionPolicies(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to fetch policies'));
    }
  }
);

export const createDeductionPolicy = createAsyncThunk(
  'teacherLateness/createPolicy',
  async (data, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.createDeductionPolicy(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const updateDeductionPolicy = createAsyncThunk(
  'teacherLateness/updatePolicy',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.updateDeductionPolicy(id, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const deleteDeductionPolicy = createAsyncThunk(
  'teacherLateness/deletePolicy',
  async (id, { rejectWithValue }) => {
    try {
      await teacherLatenessAPI.deleteDeductionPolicy(id);
      return id;
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const markTeacherAttendance = createAsyncThunk(
  'teacherLateness/markAttendance',
  async (data, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.markTeacherAttendance(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to mark attendance'));
    }
  }
);

export const fetchTeacherAttendance = createAsyncThunk(
  'teacherLateness/fetchAttendance',
  async (params, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getTeacherAttendance(params);
      return res.data;
    } catch (err) {
      console.error('[fetchTeacherAttendance] Error:', err?.response?.status, err?.response?.data || err.message);
      return rejectWithValue(extractError(err, 'Failed to fetch attendance'));
    }
  }
);

export const fetchLatenessDashboard = createAsyncThunk(
  'teacherLateness/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getLatenessDashboard();
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to fetch dashboard'));
    }
  }
);

export const fetchAdminLatenessSummary = createAsyncThunk(
  'teacherLateness/fetchAdminSummary',
  async (_, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getAdminLatenessSummary();
      return res.data;
    } catch (err) {
      console.error('[fetchAdminLatenessSummary] Error:', err?.response?.status, err?.response?.data || err.message);
      return rejectWithValue(extractError(err, 'Failed to fetch summary'));
    }
  }
);

export const fetchLatenessReport = createAsyncThunk(
  'teacherLateness/fetchReport',
  async (params, { rejectWithValue }) => {
    try {
      const res = await teacherLatenessAPI.getLatenessReport(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, 'Failed to fetch report'));
    }
  }
);

const initialState = {
  config: null,
  holidays: [],
  policies: [],
  attendanceRecords: [],
  dashboard: null,
  adminSummary: [],
  report: null,
  loading: false,
  attendanceLoading: false,
  summaryLoading: false,
  error: null,
};

const teacherLatenessSlice = createSlice({
  name: 'teacherLateness',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearAttendanceError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLatenessConfig.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLatenessConfig.fulfilled, (state, action) => { state.loading = false; state.config = action.payload; })
      .addCase(fetchLatenessConfig.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateLatenessConfig.fulfilled, (state, action) => { state.config = action.payload; })

      .addCase(fetchHolidays.pending, (state) => { state.loading = true; })
      .addCase(fetchHolidays.fulfilled, (state, action) => { state.loading = false; state.holidays = action.payload; })
      .addCase(fetchHolidays.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createHoliday.fulfilled, (state, action) => { state.holidays.push(action.payload); })

      .addCase(deleteHoliday.fulfilled, (state, action) => {
        state.holidays = state.holidays.filter((h) => h.id !== action.payload);
      })

      .addCase(fetchDeductionPolicies.pending, (state) => { state.loading = true; })
      .addCase(fetchDeductionPolicies.fulfilled, (state, action) => { state.loading = false; state.policies = action.payload; })
      .addCase(fetchDeductionPolicies.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createDeductionPolicy.fulfilled, (state, action) => { state.policies.unshift(action.payload); })
      .addCase(updateDeductionPolicy.fulfilled, (state, action) => {
        const idx = state.policies.findIndex((p) => p.id === action.payload.id);
        if (idx >= 0) state.policies[idx] = action.payload;
      })
      .addCase(deleteDeductionPolicy.fulfilled, (state, action) => {
        state.policies = state.policies.filter((p) => p.id !== action.payload);
      })

      .addCase(markTeacherAttendance.pending, (state) => { state.loading = true; })
      .addCase(markTeacherAttendance.fulfilled, (state, action) => { state.loading = false; })
      .addCase(markTeacherAttendance.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchTeacherAttendance.pending, (state) => {
        state.attendanceLoading = true;
        state.error = null;
      })
      .addCase(fetchTeacherAttendance.fulfilled, (state, action) => {
        state.attendanceLoading = false;
        state.attendanceRecords = action.payload;
        state.error = null;
      })
      .addCase(fetchTeacherAttendance.rejected, (state, action) => {
        state.attendanceLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchLatenessDashboard.pending, (state) => { state.loading = true; })
      .addCase(fetchLatenessDashboard.fulfilled, (state, action) => { state.loading = false; state.dashboard = action.payload; })
      .addCase(fetchLatenessDashboard.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchAdminLatenessSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchAdminLatenessSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.adminSummary = action.payload;
      })
      .addCase(fetchAdminLatenessSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        console.warn('[AdminSummary] Non-critical failure:', action.payload);
      })

      .addCase(fetchLatenessReport.pending, (state) => { state.loading = true; })
      .addCase(fetchLatenessReport.fulfilled, (state, action) => { state.loading = false; state.report = action.payload; })
      .addCase(fetchLatenessReport.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { clearError, clearAttendanceError } = teacherLatenessSlice.actions;
export default teacherLatenessSlice.reducer;
